import { FormattedMessage, useIntl } from 'react-intl';

import type { ChargingSessionType } from '@/api/schema';
import Money from '@/components/Money';
import type { TimeFrame } from '@/components/TimePicker/types';
import TotalCard from '@/components/ui/TotalCard';
import { formatEnergyValue } from '@/lib/utils';

export type ChargingSessionsTotalsProps = {
    timeFrame: TimeFrame;
    rows?: ChargingSessionType[] | undefined;
    currency: string;
};

const Totals = ({ timeFrame, rows, currency }: ChargingSessionsTotalsProps) => {
    const { locale } = useIntl();

    let totalEnergy = 0;
    let totalCosts = 0;
    let totalUnitPrice = 0;
    let unitPriceCount = 0;
    rows?.forEach(s => {
        totalEnergy += s?.energy ?? 0;
        totalCosts += s?.totalCost ?? 0;
        if (s?.components) {
            totalUnitPrice += s.components.unitPrice;
            unitPriceCount += 1;
        }
    });
    const { value: totalEnergyFormat, unit: totalEnergyUnit } = formatEnergyValue(totalEnergy, locale);

    // Average price per kWh
    const averagePrice = unitPriceCount > 0 ? totalUnitPrice / unitPriceCount : 0;

    return (
        <div className='margin-bottom-25 margin-top-10 display-flex width-100pct flex-column'>
            <div className='display-flex width-100pct gap-20'>
                <div className='panel width-33pct'>
                    <TotalCard
                        title={<FormattedMessage id='general.totalChargingCostsNet' />}
                        startDate={timeFrame?.startDate}
                        endDate={timeFrame?.endDate}
                    >
                        <span className='text-bold' style={{ lineHeight: 1, fontSize: '30px' }}>
                            <Money currency={currency} value={totalCosts ?? 0} />
                        </span>
                    </TotalCard>
                </div>
                <div className='panel width-33pct'>
                    <TotalCard
                        title={<FormattedMessage id='general.energyCharged' />}
                        startDate={timeFrame?.startDate}
                        endDate={timeFrame?.endDate}
                    >
                        <span>
                            <span className='text-bold' style={{ lineHeight: 1, fontSize: '30px' }}>
                                {totalEnergyFormat}
                            </span>{' '}
                            {totalEnergyUnit}
                        </span>
                    </TotalCard>
                </div>
                <div className='panel width-33pct'>
                    <TotalCard
                        title={<FormattedMessage id='general.averagePrice' />}
                        startDate={timeFrame?.startDate}
                        endDate={timeFrame?.endDate}
                    >
                        <span>
                            <span className='text-bold' style={{ lineHeight: 1, fontSize: '30px' }}>
                                <Money currency={currency} value={averagePrice ?? 0} />
                            </span>
                            {' / kWh'}
                        </span>
                    </TotalCard>
                </div>
            </div>
        </div>
    );
};

export default Totals;
